import { Form } from "react-bootstrap";
import Table from "react-bootstrap/Table";
import calendarIcon from "../../../assets/images/calenderIcon.svg";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./SummaryOfAssessment.css";

type Option = {
  value: string | null;
  label: string;
};

type Scheme = "FSC" | "PEFC" | "SFI" | "Other";

interface FormData {
  [key: string]: any;
}

interface SummaryOfAssessmentProps {
  formData: FormData;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
}

const certificationRecommendationOptions: Option[] = [
  { label: "--None--", value: "null" },
  { label: "Initial Certification is Recommended", value: "​​Initial Certification is recommended​" },
  { label: "Initial Certification is Not Recommended", value: "​​Initial Certification is not recommended​" },
  { label: "Continued Certification is Recommended", value: "​​Continued certification is recommended​" },
  { label: "Continued Certification is Not Recommended", value: "Continued certification is not recommended" },
  { label: "Re-Certification is Recommended", value: "​​Re-certification is recommended​" },
  { label: "Re-Certification is Not Recommended", value: "​​Re-certification is not recommended​" },
];

const certificationDecisionOptions: Option[] = [
  { label: "--None--", value: "null" },
  { label: "Initial Certification is Recommended", value: "​​Initial Certification is recommended​" },
  { label: "Initial Certification is Not Recommended", value: "​​Initial Certification is not recommended​" },
  { label: "Continued Certification is Recommended", value: "​​Continued certification is recommended​" },
  { label: "Continued Certification is Not Recommended", value: "Continued certification is not recommended" },
  { label: "Re-Certification is Recommended", value: "​​Re-certification is recommended​" },
  { label: "Re-Certification is Not Recommended", value: "​​Re-certification is not recommended​" },
];

const certificateTypeOptions: Option[] = [
  { label: "--None--", value: "null" },
  { label: "Multi site", value: "Multi-site" },
  { label: "Group", value: "Group" },
  { label: "N/A", value: "N/A" },
  { label: "single FSC certificate", value: "single FSC certificate" },
]
const SummaryOfAssessment: React.FC<SummaryOfAssessmentProps> = ({ formData, setFormData }) => {
  // Handlers
  const handleRecommendationChange = (value: string, scheme: Scheme) => {
    setFormData(prev => ({
      ...prev,
      [`Auditor_Certification_Review_${scheme}__c`]: value === "null" ? null : value,
    }));
  };

  const handleCommentChange = (scheme: Scheme, value: string) => {
    setFormData(prev => ({
      ...prev,
      [`Auditor_Comments_${scheme}__c`]: value === "null" ? null : value,
    }));
  };

  const handleDecisionChange = (value: string, scheme: Scheme) => {
    setFormData(prev => ({
      ...prev,
      [`SCS_Certification_Decision_${scheme}__c`]: value,
    }));
  };

  const handleAllowableSizeChange = (field: string | null) => {
    // Mapping to your data keys
    // Certificate_Type__c stores a string like "Multi-site" or "Group", so we handle this accordingly
    console.log(field)
    setFormData(prev => ({
      ...prev,
      Certificate_Type__c: field,
    }));
  };

  const handleSitesGrowthChange = (field: "sitesToAdd" | "growthRate", value: string) => {
    if (field === "growthRate") {
      // store as number if possible
      const num = parseFloat(value);
      setFormData(prev => ({
        ...prev,
        Annual_growth_rate__c: isNaN(num) ? undefined : num,
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        Allowable_Size_Limit__c: value,
      }));
    }
  };

  const handleCentralOfficeAuditProgramChange = (value: boolean) => {
    setFormData(prev => ({
      ...prev,
      Central_Office_Audit_Program__c: value,
    }));
  };

  const handleDateChange = (date: Date | string | null, field: "certificationDecisionBy" | "reportFinalizationDate") => {
    if (field === "certificationDecisionBy") {
      setFormData(prev => ({
        ...prev,
        Certification_Decision_s_By__c: date ? date : null,
      }));
    } else if (field === "reportFinalizationDate" && date instanceof Date) {
      setFormData(prev => ({
        ...prev,
        Date_Of_Report_Finalization__c: date ? date?.toISOString() : null,
      }));
    }
  };

  // Dropdowns renderers
  const auditorCertificationDropdown = (options: Option[], scheme: Scheme) => (
    <select
      className="form-select w-full h-full outline-none text-[#000000] text-sm"
      style={{ backgroundColor: "#F6F6F6" }}
      value={formData[`Auditor_Certification_Review_${scheme}__c`] || ""}
      onChange={(e) => handleRecommendationChange(e.target.value, scheme)}
    >
      {options.map((option) => (
        <option key={option.value} value={option.value ?? "null"}>{option.label}</option>
      ))}
    </select>
  );

  const certificationDecisionDropdown = (options: Option[], scheme: Scheme) => (
    <select
      className="form-select w-full h-full outline-none text-[#000000] text-sm"
      style={{ backgroundColor: "#F6F6F6", cursor: "not-allowed" }}
      value={formData[`SCS_Certification_Decision_${scheme}__c`] || ""}
      onChange={(e) => handleDecisionChange(e.target.value, scheme)}
      disabled
    >
      {options.map((option) => (
        <option key={option.value} value={option.value ?? "null"}>{option.label}</option>
      ))}
    </select>
  );

  return (
    <div className="p-4">
      {/* Auditor Certification Recommendation */}
      <Table bordered size="sm" className="text-center align-middle">
        <tbody>
          <tr>
            <td rowSpan={5} style={{ width: "281px", background: "#E5E5E5", fontWeight: 800, color: "black" }}>
              AUDITOR CERTIFICATION RECOMMENDATION
            </td>
          </tr>
          {(["FSC", "PEFC", "SFI", "Other"] as Scheme[]).map((scheme) => (
            <tr key={scheme}>
              <td style={{ width: "144px", fontWeight: 800 }}>{scheme}</td>
              <td style={{ width: "144px" }}>
                {auditorCertificationDropdown(certificationRecommendationOptions, scheme)}
              </td>
              <td>
                <Form.Control
                  style={{ fontSize: "12px", fontStyle: "italic", textAlign: "left" }}
                  type="text"
                  placeholder="If certification is not recommended, please comment:"
                  value={formData[`Auditor_Comments_${scheme}__c`] || ""}
                  onChange={(e) => handleCommentChange(scheme, e.target.value)}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {/* Certification Decision */}
      <div style={{ overflowX: "auto", maxWidth: "100%" }}>
        <Table bordered size="sm" className="text-center align-middle">
          <tbody>
            <tr>
              <td rowSpan={6} style={{ width: "281px", background: "#E5E5E5", fontWeight: 800 }}>
                <p style={{ marginBottom: 0 }}>CERTIFICATION DECISION</p>
                <span style={{ fontSize: "14px" }}>(SCS use only)</span>
              </td>
            </tr>

            {(["FSC", "PEFC", "SFI"] as Scheme[]).map((scheme) => (
              <tr key={scheme}>
                <td style={{ width: "144px", fontWeight: 800 }}>{scheme}</td>
                <td colSpan={2} style={{ textAlign: "left" }}>
                  {certificationDecisionDropdown(certificationDecisionOptions, scheme)}
                </td>
              </tr>
            ))}

            <tr>
              <td style={{ width: "144px", fontWeight: 800 }}>Other</td>
              <td colSpan={2} style={{ textAlign: "left" }}>
                <Form.Control
                  type="text"
                  style={{ cursor: "not-allowed" }}
                  placeholder="Type here"
                  value={formData.SCS_Certification_Decision_Other__c || ""}
                  onChange={(e) => handleDecisionChange(e.target.value, "Other")}
                  disabled
                />
              </td>
            </tr>

            <tr>
              <td colSpan={2} style={{ fontWeight: 800, textAlign: "left", padding: "10px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  Certification decision(s) by
                  <div className="pl-3">
                    <Form.Control
                      type="text"
                      style={{ cursor: "not-allowed" }}
                      disabled
                      onChange={(e) => handleDateChange(e.target.value, "certificationDecisionBy")}
                      value={formData.Certification_Decision_s_By__c}
                    />
                  </div>
                </div>
              </td>
              <td style={{ fontWeight: 800, textAlign: "left", padding: "10px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                  Date of report finalization
                  <div className="pl-3 position-relative m-3">
                    <DatePicker
                      selected={formData.Date_Of_Report_Finalization__c ? new Date(formData.Date_Of_Report_Finalization__c + "Z") : null}
                      onChange={(date) => handleDateChange(date, "reportFinalizationDate")}
                      placeholderText="Select date"
                      className="custom-datepicker"
                      dateFormat="d MMMM YYYY"
                      disabled
                    />
                    <img className="calendar-icon" src={calendarIcon} alt="Calendar Icon" />
                  </div>
                </div>
              </td>
            </tr>

            {/* Allowable Size Limit & Growth Rate */}
            <tr>
              <td
                colSpan={2}
                style={{
                  textAlign: "center",
                  verticalAlign: "middle",
                  padding: "20px",
                }}
              >
                <div style={{ textAlign: "left" }}>
                  <label
                    style={{
                      fontWeight: 600,
                      color: "#353535",
                      fontSize: "12px",
                      marginRight: "5px",
                      marginBottom: "20px"
                    }}
                  >
                    Certificate Type
                  </label>
                  <div className="ml-4">
                    {certificateTypeOptions.map((option) => (
                      <Form.Check key={String(option.value)}>
                        <Form.Check.Input
                          style={{ fontWeight: "400", fontSize: "14px", marginTop: "5px" }}
                          type="checkbox"

                          checked={String(formData.Certificate_Type__c) === String(option.value)}
                          onChange={(e) => handleAllowableSizeChange(option.value === "null" ? null : option.value)}
                        />
                        <Form.Check.Label className="product-categories-label">
                          {option.label}
                        </Form.Check.Label>
                      </Form.Check>
                    ))}
                  </div>
                </div>
              </td>

              <td style={{ textAlign: "left", padding: "20px" }} colSpan={2}>
                <div>
                  The organization may add up to
                  <Form.Control
                    type="text"
                    value={formData.Allowable_Size_Limit__c || ""}
                    onChange={(e) => handleSitesGrowthChange("sitesToAdd", e.target.value)}
                    className="soa__inline_input"
                  />
                  sites to the certificate before the next annual audit (
                  <Form.Control
                    className="soa__inline_input"
                    type="text"
                    value={formData.Annual_growth_rate__c || ""}
                    onChange={(e) => handleSitesGrowthChange("growthRate", e.target.value)}
                  />
                  % growth).* This number is subject to change at subsequent SCS audits.
                </div>
                <Form.Check
                  style={{ fontWeight: "400", fontSize: "14px", marginBlock: "20px", color: "#353535" }}
                  type="checkbox"
                  id="centralOfficeAuditProgram"
                  label="N/A, no Central Office Audit Program; all sites must be audited by SCS before being added to the certificate."
                  checked={formData.Central_Office_Audit_Program__c || false}
                  onChange={(e) => handleCentralOfficeAuditProgramChange(e.target.checked)}
                />

                <span style={{ fontSize: "12px" }}>*If additional certified locations or expansion beyond the maximum are desired, please contact SCS. </span>
              </td>
            </tr>

          </tbody>
        </Table>
      </div>
    </div>
  );
};

export default SummaryOfAssessment;
